import { useState } from 'react';
import { ImageIcon, Play } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { MediaFileDTO } from '@/api/generated/model';
import { isVideoMedia } from './media';
import { MediaLightbox } from './MediaLightbox';
import { safeMediaSrc } from '@/lib/mediaUrl';

interface ContentMediaCardProps {
  medias: MediaFileDTO[];
  /** If true, media is shown with grayscale + blur (unmoderated or blocked content). */
  blurred?: boolean;
}

// Media header (doc 08 §4.7): image via <img>, video via a play-badged preview that opens the
// lightbox (mux HLS player). Content media is served from the public CDN, so no blob-URL fetch.
export function ContentMediaCard({ medias, blurred }: ContentMediaCardProps) {
  const [open, setOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  const openAt = (i: number) => {
    setStartIndex(i);
    setOpen(true);
  };

  const first = medias[0];

  return (
    <Card>
      <CardContent className="p-3">
        {!first ? (
          <div className="flex h-64 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <ImageIcon className="size-8" />
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => openAt(0)}
              className={`relative block h-64 w-full overflow-hidden rounded-lg bg-muted ${blurred ? 'grayscale blur-md' : ''}`}
            >
              {isVideoMedia(first) ? (
                <>
                  {safeMediaSrc(first.thumbnailUrl) ? (
                    <img src={safeMediaSrc(first.thumbnailUrl)} alt="" className="size-full object-cover" />
                  ) : safeMediaSrc(first.url) ? (
                    // Pipeline-only uploads have no Mux thumb — use the media URL as a muted poster frame.
                    <video
                      src={safeMediaSrc(first.url)}
                      className="size-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <div className="flex size-full items-center justify-center bg-black/80" />
                  )}
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="rounded-full bg-black/60 p-3 text-white">
                      <Play className="size-6" />
                    </span>
                  </span>
                </>
              ) : safeMediaSrc(first.url) ? (
                <img src={safeMediaSrc(first.url)} alt="" className="size-full object-cover" />
              ) : (
                <div className="flex size-full items-center justify-center bg-muted text-muted-foreground">
                  <ImageIcon className="size-8" />
                </div>
              )}
            </button>

            {/* Thumbnail strip when there is more than one media item. */}
            {medias.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {medias.map((m, i) => (
                  <button
                    key={`${m.url ?? 'm'}-${i}`}
                    type="button"
                    onClick={() => openAt(i)}
                    className="relative size-16 shrink-0 overflow-hidden rounded border"
                  >
                    {isVideoMedia(m) ? (
                      <span className="flex size-full items-center justify-center bg-black/80 text-white">
                        <Play className="size-4" />
                      </span>
                    ) : safeMediaSrc(m.url) ? (
                      <img src={safeMediaSrc(m.url)} alt="" className="size-full object-cover" />
                    ) : (
                      <span className="flex size-full items-center justify-center bg-muted text-muted-foreground">
                        <ImageIcon className="size-4" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      <MediaLightbox open={open} onOpenChange={setOpen} medias={medias} initialIndex={startIndex} />
    </Card>
  );
}
